(() => {
  const cleanups = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const mobile = window.matchMedia('(max-width: 989px)');
  const clamp = (value) => Math.max(0, Math.min(1, value));

  const initMiniBuilder = (root) => {
    const input = root.querySelector('[data-mini-builder-name]');
    const belt = root.querySelector('[data-mini-builder-belt]');
    const text = root.querySelector('[data-mini-builder-text]');
    const live = root.querySelector('[data-mini-builder-live]');
    const stage = root.querySelector('[data-mini-builder-aria-template]');
    const content = root.querySelector('[data-mini-builder-content]');
    const nameLane = root.querySelector('[data-mini-builder-name-lane]');
    const rankBars = root.querySelector('[data-mini-builder-rank-bars]');
    const rankBarItems = rankBars ? [...rankBars.querySelectorAll('[data-mini-builder-rank-bar]')] : [];
    const rankButtons = [...root.querySelectorAll('[data-mini-builder-rank]')];
    const colors = [...root.querySelectorAll('[data-mini-builder-color]')];
    if (!input || !belt || !text || !live || !colors.length) return () => {};

    const defaultName = 'STEVE JOBS';
    let colorName = colors.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset.colorName || 'Gold';
    let rankCount = Number(rankButtons.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset.miniBuilderRank);
    if (!Number.isInteger(rankCount) || rankCount < 0 || rankCount > 10) rankCount = 5;
    const currentName = () => input.value.trim() || defaultName;
    const format = (template, replacements) => Object.entries(replacements).reduce((value, [key, replacement]) => value.replaceAll(`__${key}__`, String(replacement)), template);
    const rankAriaTemplate = () => stage?.dataset.miniBuilderRankAriaTemplate || '__COUNT__ gold rank bars shown';
    const rankButtonTemplate = () => stage?.dataset.miniBuilderRankButtonTemplate || 'Choose __COUNT__ rank bars';
    const fitName = () => {
      if (!nameLane) return;
      text.style.removeProperty('--preview-name-font-size');
      const baseSize = parseFloat(window.getComputedStyle(text).fontSize) || 48;
      const laneStyle = window.getComputedStyle(nameLane);
      const lanePadding = parseFloat(laneStyle.paddingTop) + parseFloat(laneStyle.paddingBottom);
      const available = Math.max(1, nameLane.clientHeight - lanePadding - 8);
      const naturalWidth = text.scrollWidth;
      if (!naturalWidth) return;
      const targetSize = Math.max(5, Math.min(baseSize * 1.35, baseSize * available / naturalWidth));
      text.style.setProperty('--preview-name-font-size', `${targetSize}px`);
      const fittedSize = Math.max(5, targetSize * Math.min(1, available / Math.max(1, text.scrollWidth)));
      if (fittedSize !== targetSize) text.style.setProperty('--preview-name-font-size', `${fittedSize}px`);
    };
    const fitAssembly = (name) => {
      if (!content) return;
      belt.style.removeProperty('--preview-assembly-height');
      belt.style.removeProperty('--preview-assembly-scale');
      const viewportHeight = belt.clientHeight;
      text.style.removeProperty('--preview-name-font-size');
      const nameSpace = Math.max(1, nameLane.clientHeight);
      const nameExtension = Math.min(300, Math.max(0, text.scrollWidth - nameSpace));
      const assemblyHeight = Math.max(1, viewportHeight * 0.88 + nameExtension);
      belt.style.setProperty('--preview-assembly-height', `${assemblyHeight}px`);
      belt.style.setProperty('--preview-assembly-scale', Math.min(1, viewportHeight * 0.88 / assemblyHeight).toFixed(3));
    };
    const renderRank = () => {
      rankBarItems.forEach((bar, index) => { bar.hidden = index >= rankCount; });
      if (rankBars) {
        rankBars.hidden = rankCount === 0;
        rankBars.dataset.count = String(rankCount);
      }
      rankButtons.forEach((button) => {
        const selected = Number(button.dataset.miniBuilderRank) === rankCount;
        button.setAttribute('aria-pressed', String(selected));
        button.dataset.selected = String(selected);
        button.setAttribute('aria-label', format(rankButtonTemplate(), { COUNT: button.dataset.miniBuilderRank }));
      });
    };
    const render = () => {
      const name = currentName();
      text.textContent = name;
      belt.dataset.nameLength = String(name.length);
      belt.classList.toggle('is-long-name', name.length > 14);
      belt.classList.toggle('is-long-text', name.length > 14);
      renderRank();
      fitAssembly(name);
      fitName();
      const baseAria = format(stage?.dataset.miniBuilderAriaTemplate || 'Black belt preview with __NAME__ in __COLOR__ thread', { NAME: name, COLOR: colorName });
      belt.setAttribute('aria-label', `${baseAria}. ${format(rankAriaTemplate(), { COUNT: rankCount })}`);
    };
    const announce = () => {
      const name = currentName();
      const baseLive = format(stage?.dataset.miniBuilderLiveTemplate || 'Preview shows __NAME__ in __COLOR__ thread.', { NAME: name, COLOR: colorName });
      live.textContent = `${baseLive} ${format(rankAriaTemplate(), { COUNT: rankCount })}`;
    };
    const chooseColor = (event) => {
      const button = event.currentTarget;
      colorName = button.dataset.colorName || 'Gold';
      belt.style.setProperty('--preview-thread', button.dataset.miniBuilderColor || '#d8b74a');
      colors.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      render();
      announce();
    };
    const chooseRank = (event) => {
      rankCount = Number(event.currentTarget.dataset.miniBuilderRank);
      if (!Number.isInteger(rankCount) || rankCount < 0 || rankCount > 10) rankCount = 5;
      render();
      announce();
    };

    input.addEventListener('input', render);
    input.addEventListener('change', announce);
    colors.forEach((button) => button.addEventListener('click', chooseColor));
    rankButtons.forEach((button) => button.addEventListener('click', chooseRank));
    const onResize = () => render();
    window.addEventListener('resize', onResize, { passive: true });
    render();
    if (window.requestAnimationFrame) window.requestAnimationFrame(fitName);
    if (document.fonts?.ready) document.fonts.ready.then(fitName);

    return () => {
      input.removeEventListener('input', render);
      input.removeEventListener('change', announce);
      colors.forEach((button) => button.removeEventListener('click', chooseColor));
      rankButtons.forEach((button) => button.removeEventListener('click', chooseRank));
      window.removeEventListener('resize', onResize);
      text.style.removeProperty('--preview-name-font-size');
      belt.style.removeProperty('--preview-assembly-height');
      belt.style.removeProperty('--preview-assembly-scale');
    };
  };

  const initCraftScrub = (root) => {
    const story = root.querySelector('[data-craft-story]');
    const stage = root.querySelector('[data-process-stage]');
    const video = root.querySelector('[data-craft-video] video');
    const labels = root.querySelectorAll('[data-craft-label]');

    if (!story || !stage || !video || reducedMotion.matches) return () => {};

    let active = false;
    let frame;
    let lastUpdate = 0;
    let scrollRange = 1;
    let primed = false;
    let priming = false;
    let failed = false;
    const updateInterval = 50;
    const measure = () => {
      scrollRange = Math.max(1, story.offsetHeight - window.innerHeight);
    };
    const showFallback = () => {
      failed = true;
      stage.dataset.craftFallback = 'true';
      stop();
    };
    const canSeek = () => !failed
      && video.readyState >= HTMLMediaElement.HAVE_METADATA
      && Number.isFinite(video.duration)
      && video.duration > 0
      && (!mobile.matches || primed);
    const prime = () => {
      if (!mobile.matches || !active || document.hidden || primed || priming || failed || video.readyState < HTMLMediaElement.HAVE_METADATA) return;
      priming = true;
      video.muted = true;
      video.playsInline = true;
      let play;
      try {
        play = video.play();
      } catch {
        showFallback();
        return;
      }
      if (!play?.then) {
        video.pause();
        primed = true;
        priming = false;
        setProgress();
        return;
      }
      play.then(() => {
        video.pause();
        primed = true;
        priming = false;
        setProgress();
      }).catch(showFallback);
    };
    const setProgress = () => {
      const progress = clamp(-story.getBoundingClientRect().top / scrollRange);
      const activeLabel = Math.min(labels.length - 1, Math.floor(progress * labels.length));
      stage.style.setProperty('--craft-progress', progress);
      stage.style.setProperty('--craft-hero-exit', clamp(progress / 0.35));
      labels.forEach((label, index) => {
        const isActive = index === activeLabel;
        label.classList.toggle('is-active', isActive);
        if (isActive) label.setAttribute('aria-current', 'step');
        else label.removeAttribute('aria-current');
      });
      if (canSeek()) {
        const targetTime = video.duration * progress;
        if (!video.seeking && Math.abs(video.currentTime - targetTime) > 0.08) video.currentTime = targetTime;
      }
    };
    const tick = (now) => {
      if (!active || document.hidden || failed) {
        frame = undefined;
        return;
      }
      if (now - lastUpdate >= updateInterval) {
        lastUpdate = now;
        setProgress();
      }
      frame = requestAnimationFrame(tick);
    };
    const start = () => {
      if (active && !document.hidden && !failed && !frame) {
        prime();
        frame = requestAnimationFrame(tick);
      }
    };
    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = undefined;
    };
    const observer = new IntersectionObserver((entries) => {
      active = entries.some((entry) => entry.isIntersecting);
      if (active) start();
      else stop();
    }, { threshold: 0 });
    const onResize = () => {
      measure();
      if (active && !document.hidden) setProgress();
    };
    const onVisibilityChange = () => {
      if (document.hidden) stop();
      else start();
    };
    const onMetadata = () => {
      prime();
      if (active) setProgress();
    };

    video.pause();
    video.muted = true;
    video.playsInline = true;
    if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
    measure();
    video.addEventListener('loadedmetadata', onMetadata);
    video.addEventListener('loadeddata', onMetadata);
    video.addEventListener('error', showFallback, { once: true });
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) setProgress();
    window.addEventListener('resize', onResize, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    observer.observe(stage);

    return () => {
      active = false;
      observer.disconnect();
      video.pause();
      video.removeEventListener('loadedmetadata', onMetadata);
      video.removeEventListener('loadeddata', onMetadata);
      video.removeEventListener('error', showFallback);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stop();
      labels.forEach((label) => {
        label.classList.remove('is-active');
        label.removeAttribute('aria-current');
      });
    };
  };

  const initHome = (root) => {
    if (!root || root.dataset.motionInitialized) return;
    root.dataset.motionInitialized = 'true';
    const cleanupMiniBuilder = initMiniBuilder(root);
    let reveal;
    if (!reducedMotion.matches) {
      root.dataset.motionReady = 'true';
      reveal = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          reveal.unobserve(entry.target);
        });
      }, { threshold: 0.14 });
      root.querySelectorAll('[data-reveal]').forEach((item) => reveal.observe(item));
    }

    const cleanupCraft = initCraftScrub(root);
    cleanups.set(root, () => {
      reveal?.disconnect();
      cleanupCraft();
      cleanupMiniBuilder();
      root.querySelector('[data-process-stage]')?.style.removeProperty('--craft-progress');
      root.querySelector('[data-process-stage]')?.style.removeProperty('--craft-hero-exit');
      root.removeAttribute('data-motion-initialized');
      root.removeAttribute('data-motion-ready');
    });
  };

  const homesIn = (scope) => [
    ...(scope.matches?.('[data-kuroobi-home]') ? [scope] : []),
    ...scope.querySelectorAll('[data-kuroobi-home]'),
  ];
  const initAll = (scope = document) => homesIn(scope).forEach(initHome);
  const teardownAll = (scope = document) => homesIn(scope).forEach((root) => {
    cleanups.get(root)?.();
    cleanups.delete(root);
  });
  const syncAll = () => {
    teardownAll();
    initAll();
  };

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardownAll(event.target));
  reducedMotion.addEventListener('change', syncAll);
  desktop.addEventListener('change', syncAll);
})();
